import { useAppSettings } from 'context/appsettingsContext';
import { useAuth } from 'context/useAuth';
import { useEffect, useState } from 'react';
import { useCollection } from './useCollection';
import { where } from 'firebase/firestore';

export interface CompanyUserInterface {
  id: string;
  userId: string;
  companyId: string;
  companyIds: string[];
}

export interface CompanyVehicleInterface {
  id: string;
  companyId: string;
  make: string;
  model: string;
  year: number;
}

export interface CompanyInterface {
  id: string;
  name: string;
  companyUsers: CompanyUserInterface[];
  vehicles: CompanyVehicleInterface[];
}

export function mapToCompanyInterface(company: any, users: CompanyUserInterface[], vehicles: CompanyVehicleInterface[]): CompanyInterface {
  return {
    id: company.id,
    name: company.data?.details?.name || "",
    vehicles: vehicles.filter(x => x.companyId === company.id),
    companyUsers: users.filter(x => x.companyIds?.includes(company.id)).map(x => { x.companyId = company.id; return x; }),
  };
}

export default function useGetCompanies(filters?: string) {
  const { authorisedUser } = useAuth();
  const settings = useAppSettings();
  const usersCollection = useCollection<any>('users');
  const vehiclesCollection = useCollection<any>('vehicles');
  const companiesCollection = useCollection<any>('companies');
  const [companies, setCompanies] = useState<CompanyInterface[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  function getQueryFilter() {
    if (authorisedUser.userRole === 'Company-Admin') {
      return [where("companyId", "in", authorisedUser.userCompanyIds)];
    } else {
      return [];
    }
  }

  useEffect(() => {
    let cancelled = false;
    async function getCompanies() {
      
      setLoading(true);

      try {

        let users: CompanyUserInterface[] = [];
        let companies: CompanyInterface[] = [];
        let vehicles: CompanyVehicleInterface[] = [];     
        
        switch (filters) {
          
          case '':
          case null:
          case undefined:
            users = (await usersCollection.queryMany([where("companyIds", "!=", null)])).map(x => ({
              id: x.id,
              userId: x.id,
              companyId: '',
              companyIds: x.data.companyIds || [],
            }));
            if (cancelled) return;
            vehicles = (await vehiclesCollection.queryMany([where("companyId", "!=", null)])).map(x => ({
              id: x.id,
              companyId: x.data.companyId || '',
              make: x.data.details.make,
              model: x.data.details.model,
              year: x.data.details.year,
            }));
            if (cancelled) return;
            companies = (await companiesCollection.queryMany(getQueryFilter())).map(x => mapToCompanyInterface(x, users, vehicles));
            break;

          default:
            const message = `Filter not handled : ${filters}`;
            console.error(message);
            alert(message);
        }

        if (cancelled) return;
        setCompanies(companies);

      } catch (error: any) {
        if (cancelled) return;
        //if (axios.isAxiosError(error)) {
          setError(error.message);
        //}
      } finally {
        if (!cancelled) {
          setLoading(false);
          sortCompanies('');
        }
      }
    }
    getCompanies();
    return () => { cancelled = true; };
  }, [authorisedUser.accessToken, filters, settings.baseURL, companiesCollection]);

  function sortCompanies(label: string) {
    switch (label) {
      case 'A - Z':
        setCompanies((prevState) => {
          return prevState.sort((a, b) =>
            a.name.localeCompare(b.name)
          );
        });
        break;
      case 'Z - A':
        setCompanies((prevState) => {
          return prevState.sort((a, b) =>
            b.name.localeCompare(a.name)
          );
        });
        break;
      default:
        setCompanies((prevState) => {
          return prevState.sort((a, b) =>
            a.name.localeCompare(b.name) // TODO:createdAt date comparison
          );
        });
    }
  }

  return {
    companies,
    loading,
    error,
    sortCompanies,
  };
}